// BUILD-042 — HR Administration navigation map.
// Authority: docs/superpowers/decisions/2026-10-06-build-042-approved-hr-administration-layout.md, as superseded for the SECONDARY RAIL by
// docs/superpowers/decisions/2026-10-24-build-042-guided-intake-admin-layout-local.md ("Overall HR Administration composition"):
//
//   The compact left rail lists exactly five destinations:
//     Employees, Company setup, Access permissions, Activity log, Correction requests.
//   "Company setup" is a landing page over the EXISTING setup destinations (Organization, Checklist templates, Benefit plans, Form Library,
//   Responsibility labels, Document history reasons, plus the visibly Planned Training catalog and Time-off policies). Every one of those
//   keeps its existing route; the rail simply highlights Company setup while you are on any of them. "Pending work" is no longer a rail entry:
//   open work is the third tab on the Employees page (/hr/admin?tab=open), and /hr/admin/pending redirects there so old links keep working.
//
// This is a SEPARATE data module from workspace/sectionGroups.ts, which maps a single Employee's workspace.
export type AdminNavKey =
  | 'employees' | 'pending' | 'requests' | 'companySetup'
  | 'org' | 'checklists' | 'benefits' | 'training' | 'documents' | 'timeoff'
  | 'labels' | 'historyReasons'
  | 'access' | 'audit';

export interface AdminNavItem {
  key: AdminNavKey;
  label: string;
  path: string;
  /** Planned destinations render an honest placeholder (no backend) — never a fake form. */
  planned?: boolean;
  /** One-line description shown on the Company setup landing. */
  blurb?: string;
  /** The permission flag in GET /hr/me `surfaces` that governs this destination (undefined flag = unknown = shown normally). UX only: the server re-checks. */
  requires?: 'mayManageChecklistTemplates' | 'mayManageDocuments' | 'mayManageBenefits';
}

export interface AdminNavGroup {
  groupLabel: string;
  items: AdminNavItem[];
}

/** The five rail destinations, in order. */
export const ADMIN_RAIL_ITEMS: AdminNavItem[] = [
  { key: 'employees', label: 'Employees', path: '/hr/admin' },
  { key: 'companySetup', label: 'Company setup', path: '/hr/admin/company-setup' },
  { key: 'access', label: 'Access permissions', path: '/hr/admin/access' },
  { key: 'audit', label: 'Activity log', path: '/hr/admin/audit' },
  { key: 'requests', label: 'Correction requests', path: '/hr/admin/correction-requests' },
];

/** The destinations the Company setup landing links to. Routes are exactly the pre-existing ones. */
export const COMPANY_SETUP_ITEMS: AdminNavItem[] = [
  { key: 'org', label: 'Organization', path: '/hr/admin/org', blurb: 'Maintain departments and review reporting relationships.' },
  { key: 'checklists', label: 'Checklist templates', path: '/hr/admin/checklists', requires: 'mayManageChecklistTemplates', blurb: 'Build reusable onboarding and offboarding checklists.' },
  // BUILD-042 simple benefits (migration 207): HR manages benefit info + per-employee eligibility.
  { key: 'benefits', label: 'Benefit plans', path: '/hr/admin/benefits', requires: 'mayManageBenefits', blurb: 'Configure plans once, then manage each employee’s eligibility.' },
  // Oct 8 22:34 addendum: "discoverable HR Admin -> Form Library".
  { key: 'documents', label: 'Form Library', path: '/hr/admin/documents', requires: 'mayManageDocuments', blurb: 'Upload, version and assign the blank forms employees sign.' },
  { key: 'labels', label: 'Responsibility labels', path: '/hr/admin/labels', requires: 'mayManageChecklistTemplates', blurb: 'Choose who normally handles each checklist task.' },
  { key: 'historyReasons', label: 'Document history reasons', path: '/hr/admin/history-reasons', requires: 'mayManageDocuments', blurb: 'Choose the reasons HR may use to move a completed document to History.' },
  { key: 'training', label: 'Training catalog', path: '/hr/admin/training', planned: true, blurb: 'Reusable training resources and requirements for your team.' },
  { key: 'timeoff', label: 'Time-off policies', path: '/hr/admin/timeoff', planned: true, blurb: 'Configure HR-side entitlement terms and request rules.' },
];

/** Label lookup for breadcrumbs, headings and the rail. */
export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  { groupLabel: 'HR Administration', items: ADMIN_RAIL_ITEMS },
  { groupLabel: 'Company setup', items: COMPANY_SETUP_ITEMS },
];

const COMPANY_SETUP_KEYS = new Set<AdminNavKey>(COMPANY_SETUP_ITEMS.map((i) => i.key));

/** True for a destination that lives under Company setup (the rail highlights Company setup for these). */
export function isCompanySetupChild(key: AdminNavKey): boolean {
  return COMPANY_SETUP_KEYS.has(key);
}

/** Which RAIL item is highlighted for a destination: a Company setup child highlights Company setup; 'pending' belongs to Employees. */
export function railKeyFor(key: AdminNavKey): AdminNavKey {
  if (isCompanySetupChild(key)) return 'companySetup';
  if (key === 'pending') return 'employees';
  return key;
}

export function adminGroupContaining(key: AdminNavKey): string {
  return ADMIN_NAV_GROUPS.find((g) => g.items.some((i) => i.key === key))?.groupLabel ?? ADMIN_NAV_GROUPS[0].groupLabel;
}

export function adminLabelFor(key: AdminNavKey): string {
  if (key === 'pending') return 'Open work';
  return ADMIN_NAV_GROUPS.flatMap((g) => g.items).find((i) => i.key === key)?.label ?? key;
}

const ADMIN_SUBTITLES: Record<AdminNavKey, string> = {
  employees: 'Manage employees and continue HR setup.',
  pending: 'Everything still open: documents waiting on employees, documents waiting for your review, and open checklist tasks.',
  requests: 'Review requested changes before official employee information changes.',
  companySetup: 'Company-wide HR configuration. Each area opens its existing screen.',
  org: 'Maintain departments and review reporting relationships.',
  checklists: 'Build a reusable set of steps for joining or leaving the company.',
  labels: 'Choose who normally handles each checklist task.',
  benefits: 'Configure plans once, then manage each employee’s eligibility and enrollment.',
  training: 'Reusable training resources and requirements for your team.',
  documents: 'Upload, version, and assign the blank forms employees sign.',
  timeoff: 'Configure HR-side entitlement terms and request rules.',
  historyReasons: 'Choose the reasons HR may use to move a completed document to History.',
  access: 'Understand who can see HR information and what they can do.',
  audit: 'A readable history of changes to HR records and setup.',
};

export function adminSubtitleFor(key: AdminNavKey): string {
  return ADMIN_SUBTITLES[key];
}
